import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router-dom";
import moment from "moment";
import styled from "styled-components";
import { SmartForm } from "ui-kit/SmartForm";
import { UploadFile } from "ui-kit/UploadFile";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { FormContent } from "./FormContent";
import { useRelease } from "../hooks/useRelease";
import { FormValues } from "./types";
import { decorators } from "./utils";
import { hasRequired, compose, hasRequiredLength, structuredValidator, hasRequiredMaxLength } from "utils/validation";
import { useNavigate, useParams } from "react-router";
import { ProgressSpinner } from "primereact/progressspinner";
import { Summary } from "./Summary";
import { toast } from "react-toastify";
import { useLabels } from "components/Labels/hooks/useLabels";
import Danger from '../../../assets/danger.svg'
import { runDraftSaveIfNeeded, setDraftSaveGuard } from "../draftSaveGuard";

export const ReleaseInfo = ({ }) => {
  const { id } = useParams();
  const { updateRelease, release, fetchRelease, fetching, error } = useRelease();
  const [singleTrack, setSingleTrack] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [cover, setCover] = useState<string | File | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { labels } = useLabels();

  const latestValuesRef = useRef<FormValues | null>(null);
  const latestHasErrorsRef = useRef<boolean>(false);
  const dirtyRef = useRef<boolean>(false);

  const isDirty = useCallback(() => {
    return !!dirtyRef.current || cover instanceof File;
  }, [cover]);

  const saveDraftOnly = useCallback(async () => {
    const values = latestValuesRef.current;
    if (!values) return;
    // If form has validation errors, don't block navigation with a failing save.
    if (latestHasErrorsRef.current) return;

    const labelId =
      (values as any).label && !isNaN(Number((values as any).label))
        ? String((values as any).label)
        : String(labels.length + 1);

    const data = {
      id: id,
      ...(singleTrack && { release_type_id: 2 }),
      title: (values as any).releaseTitle,
      version: (values as any).releaseVersion,
      artist_ids: (values as any).artists?.map((artist: any) => artist.id) ?? [],
      genre_ids: (values as any).additionalGenres,
      label_id: (values as any)?.label?.id ?? labelId,
      upc: (values as any).upc || '',
      main_genre: (values as any).main_genre?.[0],
      release_date: moment((values as any).releaseDate).format("YYYY-MM-DD"),
    };

    if ((values as any).hasPLineCLine) {
      if ((values as any).pline_date) {
        (data as any).pline_date = moment(
          moment((values as any).pline_date, moment.ISO_8601, true).isValid()
            ? (values as any).pline_date
            : "2025-01-01"
        ).format("YYYY-MM-DD");
      }
      if ((values as any).cline_date) {
        (data as any).cline_date = moment(
          moment((values as any).cline_date, moment.ISO_8601, true).isValid()
            ? (values as any).cline_date
            : "2025-01-01"
        ).format("YYYY-MM-DD");
      }
      if ((values as any).pline_value) (data as any).pline_value = (values as any).pline_value;
      if ((values as any).cline_value) (data as any).cline_value = (values as any).cline_value;
    } else {
      (data as any).pline_date = null;
      (data as any).cline_date = null;
      (data as any).pline_value = null;
      (data as any).cline_value = null;
    }

    if ((values as any).originalReleaseDate) {
      (data as any).original_release_date = moment((values as any).originalReleaseDate).format("YYYY-MM-DD");
    } else {
      (data as any).original_release_date = null;
    }
    if ((values as any).ean) (data as any).ean = (values as any).ean;

    // If user changed cover on mobile inside this step, upload it too.
    if (cover instanceof File) {
      const form = new FormData();
      form.append("cover", cover);
      await updateRelease(form);
    }

    await updateRelease(data);
    dirtyRef.current = false;
  }, [cover, id, labels.length, singleTrack, updateRelease]);

  const onSubmit = async (values: FormValues) => {
    
    const labelId =
      values.label && !isNaN(Number(values.label))
        ? String(values.label)
        : String(labels.length + 1);

    const data = {
      id: id,
      ...(singleTrack && { release_type_id: 2 }),
      title: values.releaseTitle,
      version: values.releaseVersion,
      artist_ids: values.artists.map((artist) => artist.id),
      genre_ids: values.additionalGenres,
      label_id: values?.label?.id ?? labelId,
      upc: values.upc || '',
      main_genre: values.main_genre[0],
      release_date: moment(values.releaseDate).format("YYYY-MM-DD"),
    };
    // Persist PLine/CLine state explicitly:
    // - when ON: send provided values
    // - when OFF: clear server values so Review doesn't keep showing old data
    if (values.hasPLineCLine) {
      if (values.pline_date) {
      (data as any).pline_date =  moment(
        moment(values.pline_date, moment.ISO_8601, true).isValid()
          ? values.pline_date
          : "2025-01-01"
      ).format("YYYY-MM-DD")
      }
    }
    if (values.hasPLineCLine) {
      if (values.cline_date) {
      (data as any).cline_date =moment(
        moment(values.cline_date, moment.ISO_8601, true).isValid()
          ? values.cline_date
          : "2025-01-01"
      ).format("YYYY-MM-DD")
      }
    }
    // Original release date: backend expects explicit null when empty
    if (values.originalReleaseDate) {
      (data as any).original_release_date = moment(values.originalReleaseDate).format("YYYY-MM-DD");
    } else {
      (data as any).original_release_date = null;
    }
    if (values.ean) {
      (data as any).ean = values.ean;
    }
    if (values.hasPLineCLine) {
      if (values.pline_value) (data as any).pline_value = values.pline_value;
      if (values.cline_value) (data as any).cline_value = values.cline_value;
    } else {
      // Backend API expects explicit nulls when P/C-line is disabled.
      // (undefined would omit fields entirely)
      (data as any).pline_date = null;
      (data as any).cline_date = null;
      (data as any).pline_value = null;
      (data as any).cline_value = null;
    }
    try {
      // Mobile-only UI allows changing cover here; upload it first (FormData),
      // then submit the rest of the release info as JSON.
      if (cover instanceof File) {
        const form = new FormData();
        form.append("cover", cover);
        await updateRelease(form);
      }
    } catch (e) {
      // keep existing error flow below
    }

    if (process.env.NODE_ENV !== "production") {
      // Debug payload for PLine/CLine issues
      console.log("ReleaseInfo.updateRelease payload", {
        hasPLineCLine: values.hasPLineCLine,
        pline_value: (data as any).pline_value,
        cline_value: (data as any).cline_value,
        pline_date: (data as any).pline_date,
        cline_date: (data as any).cline_date,
        full: data,
      });
    }

    await updateRelease(data)
      .then(() => {
        navigate(`/releases/new-release/${id}?step=track-upload`);
      })
      .catch((error) => {
        let message = "Something went wrong";
        let key = "";

        const errors = error?.response?.data?.errors;
        if (errors && typeof errors === "object") {
          const firstField = Object.keys(errors)[0];
          if (firstField && errors[firstField]?.[0]?.message) {
            message = errors[firstField][0].message;
            key = errors[firstField][0].code;
          }
        } else if (error?.message) {
          message = error.message;
        }

        toast.error(
          <FormattedMessage
            id={`NewRelease.ReleaseInfo.${key}`}
            defaultMessage={message}
          />
        );
        navigate(`/releases/new-release/${id}?step=release-info`);
      });
  };

  const onBack = async () => {
    try {
      await runDraftSaveIfNeeded();
    } finally {
    navigate(`/releases/new-release/${id}?step=upload-image`);
    }
  };

  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const el = document.querySelector(hash);
      el && el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);

  useEffect(() => {
    setSingleTrack(!!release?.release_type && release?.release_type === "SINGLE");
  }, [release]);

  useEffect(() => {
    // Keep cover in sync with server unless user selected a new file.
    if (cover instanceof File) return;
    setCover(release?.cover_url ?? null);
  }, [release?.cover_url]);

  useEffect(() => {
    fetchRelease({ backgroundRefresh: true }).catch(() => {
      // useRelease already sets `error`; avoid unhandled promise rejection noise
    });
  }, [fetchRelease]);

  useEffect(() => {
    setDraftSaveGuard({ isDirty, save: saveDraftOnly });
    return () => setDraftSaveGuard(null);
  }, [isDirty, saveDraftOnly]);

  // IMPORTANT: prevent react-final-form from reinitializing the whole form whenever `release`
  // object identity changes (e.g. background refresh). That was causing fields/toggles/genres
  // to "jump" or reset while user is editing.
  const initRef = useRef<FormValues | null>(null);
  const initReleaseIdRef = useRef<string | null>(null);

  useEffect(() => {
    const rid = release?.id != null ? String(release.id) : (id != null ? String(id) : null);
    if (rid && initReleaseIdRef.current !== rid) {
      initReleaseIdRef.current = rid;
      initRef.current = null;
    }
    if (release && !initRef.current) {
      initRef.current = { ...(release as any) };
    }
  }, [release, id]);

  const stableInitialValues = useMemo(() => {
    return (initRef.current ?? (release ? { ...(release as any) } : ({} as any))) as FormValues;
  }, [release]);

  if (fetching && !release) {
    return (
      <Loading>
        <ProgressSpinner />
      </Loading>
    );
  }

  if (!release && error) {
    return (
      <Loading>
        <ErrorBox>
          <ErrorTitle>Failed to load release</ErrorTitle>
          <ErrorText>{error}</ErrorText>
          <ErrorActions>
            <ErrorButton type="button" onClick={() => fetchRelease()}>
              Retry
            </ErrorButton>
            <ErrorButtonSecondary type="button" onClick={() => navigate(`/releases/new-release/${id}?step=upload-image`)}>
              Back
            </ErrorButtonSecondary>
          </ErrorActions>
        </ErrorBox>
      </Loading>
    );
  }

  return !release ? (
    <Loading>
      <ProgressSpinner />
    </Loading>
  ) : (
    <SmartForm<FormValues>
      onSubmit={onSubmit}
      decorators={[decorators]}
      initialValues={stableInitialValues}
      keepDirtyOnReinitialize
      validate={structuredValidator({
        artists: hasRequired,
        releaseTitle: compose(hasRequiredLength(3), hasRequiredMaxLength(255)),
        releaseVersion: hasRequiredMaxLength(100),
        label: hasRequired,
        main_genre: hasRequired,
        releaseDate: hasRequired,
        PLineValue: hasRequiredMaxLength(30),
        CLineValue: hasRequiredMaxLength(30),
      })}
    >
      {({ handleSubmit, values, hasValidationErrors, dirty }) => {
        latestValuesRef.current = values;
        latestHasErrorsRef.current = !!hasValidationErrors;
        dirtyRef.current = !!dirty;
        return (
          <Container>
            <Content>
              <Header
                title={
                  <FormattedMessage
                    id="NewRelease.ReleaseInfo.Title"
                    defaultMessage="Release information (Step 3/6)"
                  />
                }
                breadcrumbsText={
                  <FormattedMessage
                    id="NewRelease.ReleaseInfo.Breadcrumbs"
                    defaultMessage="Cover upload"
                  />
                }
                onBack={onBack}
                setSingleTrack={setSingleTrack}
                singleTrack={singleTrack}
              />
              <FormContent values={values} />

              <MobileCoverArt>
                <MobileCoverTitle>
                  <FormattedMessage
                    id="NewRelease.TrackUploadInfo.CoverArt"
                    defaultMessage="Cover art"
                  />
                </MobileCoverTitle>
                <UploadFile
                  width="100%"
                  height="min(424px, calc(100vw - 40px))"
                  accept="image/*,.jpg,.png"
                  maxFileSize={6000000}
                  setCover={setCover}
                  cover={cover}
                  release={release}
                  hideTrashButton
                >
                  <FormattedMessage
                    id="NewRelease.UploadImage.Input"
                    defaultMessage="Select or drag an image (jpg/png)"
                  />
                </UploadFile>
              </MobileCoverArt>

              <FooterWrapper
                onMouseEnter={() => setShowHint(true)}
                onMouseLeave={() => setShowHint(false)}
              >
                <Footer
                  nextButtonText={
                    <FormattedMessage
                      id="NewRelease.ReleaseInfo.Next"
                      defaultMessage="Go to track upload"
                    />
                  }
                  disabled={hasValidationErrors}
                  onNext={handleSubmit}
                />
                {showHint && hasValidationErrors && (
                  <HoverPopup>
                    <HoverPopupTitleBox>
                      <Danger /> Fill in the fields to continue
                    </HoverPopupTitleBox>
                    <TextBox>
                      <Text>Artists;</Text>
                      <Text>Release title;</Text>
                      <Text>Label;  </Text>
                      <Text>Release date; </Text>
                      <Text>Primary genre</Text>
                    </TextBox>
                  </HoverPopup>
                )}
              </FooterWrapper>
            </Content>
            <DesktopSummary>
              <Summary coverUrl={release.cover_url} />
            </DesktopSummary>
          </Container>
        );
      }}
    </SmartForm>
  );
};

const Container = styled.div`
  width: 100%;
  background: #e9e9e9;
  display: flex;
  gap: 17px;

  /* Mobile: no right sidebar; everything is inside Content */
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const Content = styled.div`
  width: 100%;
  border-radius: 30px;
  background: #ffffff;
  padding: 38px 20px 30px;
`;

const DesktopSummary = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileCoverArt = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 18px;
    margin-bottom: 18px;
  }
`;

const MobileCoverTitle = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0%;
  color: #000000;
`;

const Loading = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const ErrorBox = styled.div`
  width: min(520px, 92vw);
  background: #ffffff;
  border-radius: 16px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ErrorTitle = styled.div`
  font-family: SF Pro Display;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: #000000;
`;

const ErrorText = styled.div`
  font-family: SF Pro Display;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.7);
  word-break: break-word;
`;

const ErrorActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ErrorButton = styled.button`
  border: 1px solid #6777ff;
  background: #6777ff;
  color: #ffffff;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
`;

const ErrorButtonSecondary = styled.button`
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: #ffffff;
  color: rgba(0, 0, 0, 0.75);
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
`;

// 👇 նոր մաս՝ hover popup-ի համար
const FooterWrapper = styled.div`
  width:100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  display: inline-block;
`;

const HoverPopup = styled.div`
  position: absolute;
  bottom: 120%;
  right: 0;
  width: 236px;
  height: auto;
  opacity: 1;
  border-radius: 15px;
  background: rgba(226, 229, 255, 1);
  box-shadow: 0px 0px 10px 0px rgba(145, 145, 145, 0.47);
  padding: 17px 21px 14px 14px;
  display: flex;
  flex-direction: column;
  gap:9px;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HoverPopupTitleBox = styled.div`
  display: flex;
  gap:6px;
  font-family: SF Pro Display;
  font-weight: 600;
  font-style: Semibold;
  font-size: 15px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: 0%;
  color:rgba(0, 0, 0, 1);
`

const Text = styled.div`
  font-family: SF Pro Display;
  font-weight: 400;
  font-style: Regular;
  font-size: 15px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: 0%;
  color:rgba(0, 0, 0, 1);
`
const TextBox = styled.div`
`